import React from 'react';
import isNil from 'lodash/isNil';
import { graphql, useFragment } from 'react-relay';
import { Text } from '@atlaskit/primitives/compiled';
import { TagItem } from '@atlassian/jira-list-with-popup/src/ui/tag-item/index.tsx';
import { useIntl } from '@atlassian/jira-intl';
import type { labelGroupHeader_issueTableGrouping$key } from '@atlassian/jira-relay/src/__generated__/labelGroupHeader_issueTableGrouping.graphql';
import { expVal } from '@atlassian/jira-feature-experiments';
import { memoizeGetAutoGeneratedColorForLabelsTag } from '@atlassian/jira-option-color-picker/src/option-color-generator/index.tsx';
import { getColorMetadata } from '@atlassian/jira-option-color-picker/src/color-picker/utils/index.tsx';
import messages from './messages.tsx';
import type { LabelsValue } from './types.tsx';

export type Props = {
	labelGroupHeader: labelGroupHeader_issueTableGrouping$key | null;
};

const isColorfulLabelsEnabled = () =>
	expVal('adding-color-to-labels-field-in-jira-experiment', 'isColoredLabelsEnabled', false) ||
	expVal('adding-color-to-labels-field-experiment-milestone2', 'isColorfulNewLabelsEnabled', false);

// Logic inherited from src/packages/issue/fields/platform/labels/readview-full/src/ui/labels/index.tsx
const getLabelColor = (label: LabelsValue) => {
	if (
		expVal(
			'adding-color-to-labels-field-experiment-milestone2',
			'isColorfulNewLabelsEnabled',
			false,
		)
	) {
		const colorName = label?.color?.colorKey;
		if (!colorName) {
			return memoizeGetAutoGeneratedColorForLabelsTag(label?.name || '');
		}
		const { tagColor } = getColorMetadata(colorName);
		return tagColor;
	}
	if (expVal('adding-color-to-labels-field-in-jira-experiment', 'isColoredLabelsEnabled', false)) {
		return memoizeGetAutoGeneratedColorForLabelsTag(label?.name || '');
	}
};

export const LabelGroupHeader = ({ labelGroupHeader }: Props) => {
	const data = useFragment<labelGroupHeader_issueTableGrouping$key>(
		graphql`
			fragment labelGroupHeader_issueTableGrouping on JiraJqlLabelFieldValue {
				label {
					name
					color {
						colorKey
					}
				}
			}
		`,
		labelGroupHeader,
	);

	const { formatMessage } = useIntl();

	return !isNil(data?.label?.name) ? (
		<TagItem
			name={data.label.name}
			color={getLabelColor(data.label)}
			isColored={isColorfulLabelsEnabled()}
		/>
	) : (
		<Text>{formatMessage(messages.noLabel)}</Text>
	);
};
